import React from 'react';
import {
  ChevronDown, ChevronRight, Wallet, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import { CaisseJourBadge } from './CentralBadges';
import { formatMontant } from './nosDepensesUtils';

/**
 * Qui tient sa caisse ce matin, et qui ne l'a pas ouverte.
 *
 * L'écran ne montrait que les caisses REMISES : une caisse ouverte restait
 * invisible jusqu'à sa remise, et une caisse jamais ouverte n'existait nulle
 * part. On ne pouvait donc pas voir ce qui se regarde le matin.
 *
 * Les caisses OUBLIÉES — restées ouvertes un jour passé — remontent en tête
 * avec leur âge : une caisse ouverte depuis trois semaines est plus urgente
 * qu'une caisse non ouverte ce matin.
 */

// Le gabarit des colonnes, déclaré UNE FOIS : l'en-tête et les lignes s'y
// réfèrent tous les deux, seule façon qu'une colonne tombe au même endroit.
const GRILLE_JOUR = 'grid grid-cols-[minmax(200px,1.3fr)_160px_110px_90px_80px_repeat(4,110px)] gap-x-4 items-center';

const jourCourt = (valeur) => {
  if (!valeur) return '-';
  const texte = String(valeur);
  // Une DATE SQL arrive en « AAAA-MM-JJ » ou en Date posé à minuit local :
  // on découpe la chaîne plutôt que de repasser par un fuseau, qui décalerait
  // la journée d'un jour.
  const iso = texte.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('fr-FR');
};

const heureCourte = (valeur) => {
  if (!valeur) return null;
  const d = new Date(valeur);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const Resume = ({ resume }) => {
  if (!resume) return null;
  const morceaux = [
    resume.oubliees ? `${resume.oubliees} oubliée${resume.oubliees > 1 ? 's' : ''}` : null,
    `${resume.ouvertes} ouverte${resume.ouvertes > 1 ? 's' : ''}`,
    resume.remises ? `${resume.remises} remise${resume.remises > 1 ? 's' : ''}` : null,
    resume.validees ? `${resume.validees} validée${resume.validees > 1 ? 's' : ''}` : null,
    resume.non_ouvertes ? `${resume.non_ouvertes} pas ouverte${resume.non_ouvertes > 1 ? 's' : ''}` : null
  ].filter(Boolean);

  return <span className="text-sm text-gray-500">{morceaux.join(' · ')}</span>;
};

const LigneCaisse = ({ ligne }) => {
  const estOubliee = ligne.etat === 'oubliee';
  const estNonOuverte = ligne.etat === 'non_ouverte';
  const heure = heureCourte(ligne.ouverte_le);

  return (
    <div
      className={`${GRILLE_JOUR} min-w-[1180px] rounded-lg border-2 px-4 py-3 ${
        estOubliee
          ? 'border-amber-300 bg-amber-50/60'
          : estNonOuverte
            ? 'border-dashed border-gray-300 bg-gray-50/60'
            : 'border-[#246990]/20 bg-white'
      }`}
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-900">{ligne.gestionnaire}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {ligne.plus_habilite && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700">
              <AlertTriangle size={11} />
              plus habilité à tenir une caisse
            </span>
          )}
          {ligne.gestionnaire_statut && ligne.gestionnaire_statut !== 'actif' && (
            <span className="text-[11px] font-medium text-red-700">{ligne.gestionnaire_statut}</span>
          )}
          {estNonOuverte && ligne.derniere_journee && (
            <span className="text-[11px] text-gray-500">
              dernière caisse le {jourCourt(ligne.derniere_journee)}
            </span>
          )}
        </div>
      </div>

      <div>
        <CaisseJourBadge etat={ligne.etat} />
        {estOubliee && (
          <p className="mt-1 text-[11px] font-medium text-amber-800">
            ouverte depuis {ligne.age_jours} jour{ligne.age_jours > 1 ? 's' : ''}
            {ligne.autre_annee ? ' — autre année scolaire' : ''}
          </p>
        )}
      </div>

      <span className="text-sm text-gray-700">{jourCourt(ligne.date_caisse)}</span>
      <span className="text-sm text-gray-700">{heure || '-'}</span>
      <span className="text-sm text-gray-700">{estNonOuverte ? '-' : ligne.nb_transactions}</span>

      <span className="text-right text-sm font-medium text-emerald-700">
        {estNonOuverte ? '-' : formatMontant(ligne.total_encaissements)}
      </span>
      <span className="text-right text-sm text-gray-700">
        {estNonOuverte ? '-' : formatMontant(ligne.total_dotations)}
      </span>
      <span className="text-right text-sm text-red-700">
        {estNonOuverte ? '-' : formatMontant(ligne.total_depenses)}
      </span>
      <span
        className="text-right text-sm font-bold text-gray-900"
        title={ligne.totaux_en_direct
          ? "Caisse non clôturée : montants calculés à l'instant depuis ses opérations"
          : undefined}
      >
        {estNonOuverte ? '-' : formatMontant(ligne.solde_theorique)}
      </span>
    </div>
  );
};

const CaissesDuJourSection = ({
  isExpanded, onToggleSection, isLoading, lignes = [], resume, onRafraichir
}) => {
  const oubliees = lignes.filter((l) => l.etat === 'oubliee');

  return (
    <div className="px-6">
      <Card>
        <CardHeader className="cursor-pointer transition-colors hover:bg-gray-50" onClick={onToggleSection}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown size={24} className="text-gray-600" />
              ) : (
                <ChevronRight size={24} className="text-gray-600" />
              )}
              <div>
                <CardTitle className="flex items-center gap-2 text-xl text-[#246990]">
                  <Wallet size={24} />
                  Caisses du jour
                </CardTitle>
                <p className="mt-1 text-sm text-gray-500">
                  qui tient sa caisse aujourd'hui, et qui ne l'a pas ouverte
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Resume resume={resume} />
              {onRafraichir && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={isLoading}
                  onClick={(e) => { e.stopPropagation(); onRafraichir(); }}
                >
                  <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="space-y-4 pt-6">
            {oubliees.length > 0 && (
              <div className="flex items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
                <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-700" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    {oubliees.length === 1
                      ? 'Une caisse est restée ouverte un jour passé'
                      : `${oubliees.length} caisses sont restées ouvertes un jour passé`}
                  </p>
                  <p className="mt-0.5 text-sm text-amber-800">
                    Tant qu'une caisse de l'année en cours n'est pas clôturée, son détenteur ne
                    peut pas ouvrir celle du jour — et ne peut donc recevoir aucune dotation. Une
                    caisse restée ouverte sur une autre année scolaire ne bloque rien, mais reste
                    une journée jamais arrêtée.
                  </p>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="flex items-center space-x-3">
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#246990] border-t-transparent"></div>
                  <span className="font-medium text-[#246990]">Chargement...</span>
                </div>
              </div>
            ) : lignes.length === 0 ? (
              <EtatVide
                icone={Wallet}
                titre="Aucune caisse à suivre aujourd'hui"
                description="Personne n'a tenu de caisse ces douze derniers mois, ou aucune personne habilitée n'est en poste."
              />
            ) : (
              <div className="overflow-x-auto">
                {/* Neuf colonnes ne tiennent pas sous 1180 px : la charte
                    demande qu'un contenu large défile chez lui plutôt que
                    d'écraser la page. `px-[18px]` compense le `border-2` des
                    lignes, dont le contenu commence 2 px plus loin. */}
                <div className={`${GRILLE_JOUR} min-w-[1180px] border-b border-gray-200 px-[18px] pb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500`}>
                  <span>Personne</span>
                  <span>État</span>
                  <span>Journée</span>
                  <span>Ouverte à</span>
                  <span>Opér.</span>
                  <span className="text-right">Encaissements</span>
                  <span className="text-right">Dotations</span>
                  <span className="text-right">Dépenses</span>
                  <span className="text-right">Solde attendu</span>
                </div>

                <div className="mt-3 space-y-3">
                  {lignes.map((ligne) => (
                    <LigneCaisse
                      key={ligne.caisse_id ? `caisse-${ligne.caisse_id}` : `sans-${ligne.gestionnaire_id}`}
                      ligne={ligne}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default CaissesDuJourSection;
